import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import DataTable from '../../components/Common/DataTable';
import api from '../../services/api';

const formatAction = (value) => {
  if (!value) return '-';
  return value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Show the full log with no pagination — a generous cap that still guards the browser
// from an unbounded render if the table ever grows very large.
const ALL_LIMIT = 100000;

const GroupLogs = () => {
  const { groupId } = useParams();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: ALL_LIMIT,
        ...(search ? { search } : {})
      };
      const response = await api.get(`/groups/${groupId}/logs`, { params });
      if (response.data.success) {
        setLogs(response.data.data);
        setTotal(response.data.total);
      }
    } catch (err) {
      setError('Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [groupId, search]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  // These logs can arrive from another admin or the external ikigai integration,
  // not just this browser tab — poll quietly in the background so new entries
  // show up without a manual refresh. No spinner, no error toast on a missed poll.
  useEffect(() => {
    const interval = setInterval(() => {
      // Skip while the session is gone/tearing down — avoids a burst of 401s.
      if (!localStorage.getItem('token')) return;
      const params = {
        limit: ALL_LIMIT,
        ...(search ? { search } : {})
      };
      api.get(`/groups/${groupId}/logs`, { params })
        .then(response => {
          if (response.data.success) {
            setLogs(response.data.data);
            setTotal(response.data.total);
          }
        })
        .catch(err => console.error('Background log refresh failed:', err.message));
    }, 15000);
    return () => clearInterval(interval);
  }, [groupId, search]);

  const columns = [
    {
      key: 'date',
      label: 'Date & Time',
      minWidth: 160,
      render: (value) => {
        const formatted = new Date(value).toLocaleString('en-GB', {
          day: '2-digit', month: '2-digit', year: '2-digit',
          hour: '2-digit', minute: '2-digit',
          timeZone: 'UTC'
        });
        return (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Typography variant="body2">{formatted}</Typography>
            <Typography variant="caption" color="text.secondary">GMT</Typography>
          </Box>
        );
      }
    },
    {
      key: 'task',
      label: 'Action',
      minWidth: 130,
      render: (value) => <Typography variant="body2">{formatAction(value)}</Typography>
    },
    {
      key: 'by_who',
      label: 'User',
      minWidth: 180,
      render: (value) => <Typography variant="body2">{value || '-'}</Typography>
    },
    {
      key: 'ip_address',
      label: 'IP Address',
      minWidth: 120,
      render: (value) => value || '-'
    },
    {
      key: 'details',
      label: 'Details',
      render: (value) => value || '-'
    }
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography variant="h4" fontWeight={600}>Group Logs</Typography>
          {total > 0 && (
            <Typography variant="h6" color="text.secondary" fontWeight={400}>
              ({total.toLocaleString()})
            </Typography>
          )}
        </Box>
        <TextField
          size="small"
          placeholder="Search logs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <DataTable
          columns={columns}
          data={logs}
          loading={loading}
          total={total}
          filters={[]}
          filterValues={{}}
          onFilterChange={undefined}
          emptyMessage="No logs found"
          showSearch={false}
          showRefresh={false}
          showExport={false}
          sortable={false}
          hideToolbar={true}
          hidePagination={true}
          fullHeight={true}
          dense={true}
        />
      </Box>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupLogs;
